import React, { useState } from "react";
import { Send, Check, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const initial = {
  full_name: "",
  email: "",
  phone: "",
  preferred_date: "",
  interested_piece: "",
  message: "",
};

export default function ConciergeForm() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/concierge", form);
      setDone(true);
      toast.success("Sent — we'll reply within 24 hours.");
      setForm(initial);
    } catch (err) {
      toast.error("Could not send. Please verify your details.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="concierge"
      className="bg-[var(--mn-ink)] text-[var(--mn-bg)] relative"
      data-testid="concierge-section"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-24 md:py-40 grid grid-cols-12 gap-8 md:gap-16">
        <div className="col-span-12 md:col-span-5">
          <div className="inline-flex items-center gap-2 text-white/60 text-xs uppercase tracking-[0.32em]">
            <MessageSquare size={14} strokeWidth={1.4} />
            Style Advice
          </div>
          <h2 className="font-display text-4xl md:text-6xl mt-6 leading-[1.02] tracking-tighter">
            Need help <em className="font-light italic">choosing?</em>
          </h2>
          <p className="mt-8 text-white/70 leading-relaxed max-w-md">
            Tell us what you're shopping for — a designer, a colour, a gift, an
            occasion — and we'll reply within 24 hours with hand-picked suggestions
            from the current collection.
          </p>
          <ul className="mt-10 space-y-3 text-sm">
            <Bullet>Personal recommendations from our buying team</Bullet>
            <Bullet>100% authentic — money-back guarantee</Bullet>
            <Bullet>Free worldwide shipping on orders over $200</Bullet>
            <Bullet>14-day returns in original condition</Bullet>
          </ul>
        </div>

        <div className="col-span-12 md:col-span-7">
          {done ? (
            <div className="border border-white/20 p-10 md:p-14" data-testid="concierge-success">
              <div className="inline-flex items-center gap-2 text-white/70 text-xs uppercase tracking-[0.32em]">
                <Check size={16} strokeWidth={1.6} /> Received
              </div>
              <p className="font-display text-4xl md:text-5xl mt-6 leading-[1.05]">
                Thank you. We'll be <em className="font-light italic">in touch.</em>
              </p>
              <p className="mt-6 text-white/60 text-sm max-w-md">
                You'll receive a personal reply within 24 hours from a member of the Elegant Affairs team.
              </p>
              <button
                onClick={() => setDone(false)}
                className="mt-10 text-xs uppercase tracking-[0.22em] border-b border-white/40 pb-1 hover:border-white"
                data-testid="concierge-new-request"
              >
                Send another message
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="grid grid-cols-2 gap-x-6 gap-y-8" data-testid="concierge-form">
              <DarkField label="Full name" required value={form.full_name} onChange={set("full_name")} testid="concierge-name" full />
              <DarkField label="Email" type="email" required value={form.email} onChange={set("email")} testid="concierge-email" />
              <DarkField label="Phone" value={form.phone} onChange={set("phone")} testid="concierge-phone" />
              <DarkField label="Bag of interest" placeholder="e.g. Onyx Tote, Cognac Crossbody…" value={form.interested_piece} onChange={set("interested_piece")} testid="concierge-piece" full />
              <div className="col-span-2">
                <label className="block">
                  <span className="text-[0.7rem] uppercase tracking-[0.32em] text-white/50 block mb-3">How can we help?</span>
                  <textarea
                    rows="4"
                    placeholder="Style, occasion, gift recipient, budget — anything that helps us pick the right bag."
                    value={form.message}
                    onChange={set("message")}
                    className="w-full bg-transparent border-b border-white/30 py-3 text-base placeholder:text-white/30 focus:outline-none focus:border-white"
                    data-testid="concierge-message"
                  />
                </label>
              </div>
              <div className="col-span-2 flex flex-col sm:flex-row sm:items-center justify-between gap-6 pt-4">
                <p className="text-[11px] uppercase tracking-[0.22em] text-white/40">
                  Replies within 24 hours · No spam, ever
                </p>
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-[var(--mn-bg)] text-[var(--mn-ink)] px-10 py-4 uppercase tracking-[0.22em] text-xs font-medium hover:bg-white transition-colors disabled:opacity-60 inline-flex items-center gap-3"
                  data-testid="concierge-submit"
                >
                  {loading ? "Sending…" : "Send Message"}
                  {!loading && <Send size={14} strokeWidth={1.5} />}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

function DarkField({ label, testid, full, ...props }) {
  return (
    <label className={`block ${full ? "col-span-2" : "col-span-2 md:col-span-1"}`}>
      <span className="text-[0.7rem] uppercase tracking-[0.32em] text-white/50 block mb-3 inline-flex items-center gap-2">
        {label}
      </span>
      <input
        className="w-full bg-transparent border-b border-white/30 py-3 text-base placeholder:text-white/30 focus:outline-none focus:border-white text-white [color-scheme:dark]"
        data-testid={testid}
        {...props}
      />
    </label>
  );
}

function Bullet({ children }) {
  return (
    <li className="flex items-start gap-3 text-white/80">
      <span className="text-[var(--mn-bg)] mt-2 inline-block w-1.5 h-1.5 bg-[var(--mn-bg)] rounded-full" />
      {children}
    </li>
  );
}
